// ============================================================
// TATHYA DMS - Access Control List
// Version: 1.3.0
// Date: 2026-04-30
// Change (v1.3): Remove can_manage_access delegation feature
// Change (v1.2): Permission presets (Read Only / Contributor / Full Access)
// Change (v1.1): Support delegated access management.
//         - Users with can_manage_access can open this page and
//           grant/revoke permissions (not just the owner).
//         - New "Manage Access" permission that the OWNER can
//           delegate. Delegates see the column read-only — they
//           cannot promote further delegates (anti-escalation).
//         - User picker now uses the directory endpoint (visible
//           to any authenticated user), not User Master's list.
//         - "Edit PDF" removed from the UI per CEO Clause 6.2
//           (upload+delete effectively covers edit). The backend
//           column still exists; old grants are preserved.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, getPermissions, setPermission, deletePermission, getUserDirectory } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Row permissions — per database permissions the owner/delegate
// can toggle. `ownerOnly: true` means the toggle is only editable
// by the owner or platform/super admin; delegates see it read-only.
const PERMISSIONS = [
  { key: 'can_add',           label: 'Add Records' },
  { key: 'can_edit',          label: 'Edit Records' },
  { key: 'can_delete',        label: 'Delete Records' },
  { key: 'can_upload_pdf',    label: 'Upload PDF' },
  { key: 'can_delete_pdf',    label: 'Delete PDF' },
];

// The full set of permission keys the POST body must include, even
// ones we don't show in the grid (e.g. can_edit_pdf, retired in UI).
// Missing keys would be sent as undefined and overwrite existing
// values with false, so we preserve them explicitly from the record.
const ALL_PERM_KEYS = [
  'can_add', 'can_edit', 'can_delete',
  'can_upload_pdf', 'can_edit_pdf', 'can_delete_pdf'
];

const ACL = () => {
  const { org_slug, db_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [database, setDatabase] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [newPerms, setNewPerms] = useState({
    can_add: false, can_edit: false, can_delete: false,
    can_upload_pdf: false, can_delete_pdf: false,
    can_manage_access: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(null);

  // Is the current user the database OWNER or a platform/super admin?
  // Only they can toggle the "Manage Access" flag. Delegates can
  // grant/revoke other permissions but not promote further delegates.
  const isOwnerOrAdmin =
    database &&
    (Number(database.owner_id) === Number(user?.id) ||
     ['platform_admin', 'super_admin'].includes(user?.role));

  useEffect(() => {
    const load = async () => {
      try {
        const [dbRes, permRes, userRes] = await Promise.all([
          getDatabase(org_slug, db_id),
          getPermissions(org_slug, db_id),
          getUserDirectory(org_slug)
        ]);
        setDatabase(dbRes.data);
        setPermissions(permRes.data);
        setUsers(userRes.data);
      } catch (err) {
        // 403 from getPermissions = user has no manage-access rights
        if (err.response?.status === 403) {
          setError('You do not have permission to manage access for this database.');
        } else {
          setError('Failed to load');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [org_slug, db_id]);

  // Users not yet added to ACL. Also excludes:
  //   - the current logged-in user (can't grant yourself access —
  //     you either already have it as owner, or you're a delegate
  //     who can't promote themselves)
  //   - the database owner (already has full access by ownership;
  //     showing them in the picker is confusing and enables a
  //     super_admin to accidentally "grant" access to the owner).
  //   - super_admin / platform_admin (they bypass the ACL entirely)
  // (Apr 24 — Item 3.)
  const availableUsers = users.filter(u =>
    !permissions.find(p => p.user_id === u.id) &&
    u.role !== 'super_admin' &&
    u.role !== 'platform_admin' &&
    Number(u.id) !== Number(user?.id) &&
    Number(u.id) !== Number(database?.owner_id)
  );

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return setError('Please select a user');
    setError('');
    setSaving('new');
    try {
      const res = await setPermission(org_slug, db_id, {
        user_id: parseInt(selectedUser),
        ...newPerms
      });
      const picked = users.find(u => u.id === parseInt(selectedUser));
      setPermissions(prev => [...prev, { ...res.data, User: picked }]);
      setShowAddUser(false);
      setSelectedUser('');
      setNewPerms({
        can_add: false, can_edit: false, can_delete: false,
        can_upload_pdf: false, can_delete_pdf: false,
        can_manage_access: false
      });
      setSuccess(`Access granted to ${picked?.name}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add user');
    } finally {
      setSaving(null);
    }
  };

  const handleTogglePermission = async (permId, userId, key, currentValue) => {
    setSaving(permId);
    try {
      const perm = permissions.find(p => p.id === permId);
      // Build payload with ALL current values so nothing gets nulled.
      // We include can_edit_pdf even though the UI has retired it,
      // preserving any grants made before Clause 6.2 took effect.
      const payload = { user_id: userId };
      for (const k of ALL_PERM_KEYS) {
        payload[k] = k === key ? !currentValue : !!perm[k];
      }
      await setPermission(org_slug, db_id, payload);
      setPermissions(prev => prev.map(p =>
        p.id === permId ? { ...p, [key]: !currentValue } : p
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permission');
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div style={styles.center}>Loading...</div>;

  // Access-denied state — show a clear message with a way out
  const handleRevoke = async (permId, userName) => {
    if (!window.confirm(`Remove ${userName}'s access to this database?`)) return;
    setSaving(permId);
    try {
      await deletePermission(org_slug, db_id, permId);
      setPermissions(prev => prev.filter(p => p.id !== permId));
    } catch (err) {
      setError('Failed to revoke access');
    } finally {
      setSaving(null);
    }
  };

  if (error && permissions.length === 0 && !database) {
    return (
      <div style={styles.center}>
        <h2 style={{color:'#C0392B'}}>Access Denied</h2>
        <p style={{color:'#595959',marginTop:'8px',marginBottom:'16px'}}>{error}</p>
        <button className="btn btn-secondary"
          onClick={() => navigate(`/${org_slug}/databases/${db_id}`)}>
          ← Back to database
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/${org_slug}/databases/${db_id}`)}
            style={{ marginBottom: '8px', fontSize: '12px' }}
          >
            ← Back to {database?.name}
          </button>
          <h1 style={styles.title}>Access Control</h1>
          <p style={styles.subtitle}>
            {database?.db_number} · {database?.name}
            {!isOwnerOrAdmin && (
              <span style={styles.delegateBadge}>
                Delegate — you can grant / revoke access
              </span>
            )}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setShowAddUser(!showAddUser); setError(''); }}
        >
          {showAddUser ? 'Cancel' : '+ Grant Access'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={styles.errorBox}>
          {error}
          <button onClick={() => setError('')} style={styles.closeBtn}>✕</button>
        </div>
      )}
      {success && (
        <div style={styles.successBox}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.closeBtn}>✕</button>
        </div>
      )}

      {/* Add User Form */}
      {showAddUser && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <h3 style={styles.formTitle}>Grant Database Access</h3>
          <form onSubmit={handleAddUser}>
            <div className="form-group">
              <label>Select User *</label>
              <select
                value={selectedUser}
                onChange={e => setSelectedUser(e.target.value)}
                required
              >
                <option value="">-- Select a user --</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
              {availableUsers.length === 0 && (
                <p style={styles.hint}>
                  All users in your organisation already have access, or there are no other users yet.
                </p>
              )}
            </div>

            <div style={styles.permGrid}>
              <p style={styles.permLabel}>Initial Permissions:</p>
              <div style={styles.presetBar}>
                <span style={styles.presetLabel}>Quick set:</span>
                {[
                  { label: 'Read Only',    perms: { can_add: false, can_edit: false, can_delete: false, can_upload_pdf: false, can_delete_pdf: false } },
                  { label: 'Contributor', perms: { can_add: true,  can_edit: true,  can_delete: false, can_upload_pdf: true,  can_delete_pdf: false } },
                  { label: 'Full Access', perms: { can_add: true,  can_edit: true,  can_delete: true,  can_upload_pdf: true,  can_delete_pdf: true,  can_manage_access: false } },
                ].map(({ label, perms }) => (
                  <button
                    key={label}
                    type="button"
                    className="btn btn-secondary"
                    style={styles.presetBtn}
                    onClick={() => setNewPerms(prev => ({ ...prev, ...perms }))}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {PERMISSIONS.map(p => {
                const locked = false;
                return (
                  <label
                    key={p.key}
                    style={{
                      ...styles.permCheck,
                      ...(locked ? styles.permCheckLocked : {})
                    }}
                    title={locked ? 'Only the database owner can delegate this.' : ''}
                  >
                    <input
                      type="checkbox"
                      checked={newPerms[p.key]}
                      disabled={locked}
                      onChange={() => setNewPerms(prev => ({
                        ...prev, [p.key]: !prev[p.key]
                      }))}
                      style={{ marginRight: '8px' }}
                    />
                    {p.label}
                    {p.ownerOnly && <span style={styles.ownerTag}>owner</span>}
                  </label>
                );
              })}
            </div>

            <div style={styles.formActions}>
              <button type="button" className="btn btn-secondary"
                onClick={() => setShowAddUser(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary"
                disabled={saving === 'new'}>
                {saving === 'new' ? 'Granting...' : 'Grant Access'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Permissions Table */}
      <div className="card">
        <h3 style={styles.formTitle}>
          Users with Access
          <span style={styles.count}>{permissions.length} users</span>
        </h3>

        {permissions.length === 0 ? (
          <div style={styles.empty}>
            No users have been granted access yet.
            Click "+ Grant Access" to add users.
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  {PERMISSIONS.map(p => (
                    <th key={p.key} style={styles.thPerm}>
                      {p.label}
                      {p.ownerOnly && <span style={styles.ownerTagHeader}>owner</span>}
                    </th>
                  ))}
                  <th style={styles.thPerm}>Revoke</th>
                </tr>
              </thead>
              <tbody>
                {permissions.map(perm => (
                  <tr key={perm.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.userName}>
                        {perm.User?.name}
                      </div>
                      <div style={styles.userEmail}>
                        {perm.User?.email}
                      </div>
                    </td>
                    {PERMISSIONS.map(p => {
                      const locked = p.ownerOnly && !isOwnerOrAdmin;
                      return (
                        <td key={p.key} style={styles.tdPerm}>
                          <button
                            onClick={() => !locked && handleTogglePermission(
                              perm.id, perm.user_id, p.key, perm[p.key]
                            )}
                            style={{
                              ...styles.toggle,
                              background: perm[p.key] ? '#1E7B34' : '#e0e0e0',
                              opacity: (saving === perm.id || locked) ? 0.6 : 1,
                              cursor: locked ? 'not-allowed' : 'pointer'
                            }}
                            disabled={saving === perm.id || locked}
                            title={
                              locked
                                ? 'Only the database owner can change this'
                                : perm[p.key] ? 'Click to revoke' : 'Click to grant'
                            }
                          >
                            {perm[p.key] ? '✓' : '✗'}
                          </button>
                        </td>
                      );
                    })}
                    <td style={styles.tdPerm}>
                        <button
                          onClick={() => handleRevoke(perm.id, perm.User?.name)}
                          disabled={saving === perm.id}
                          style={{
                            ...styles.toggle,
                            background: saving === perm.id ? '#e0e0e0' : '#C0392B',
                            color: '#fff',
                            fontSize: '11px',
                            padding: '2px 10px',
                            borderRadius: '4px',
                            cursor: saving === perm.id ? 'not-allowed' : 'pointer',
                            opacity: saving === perm.id ? 0.6 : 1,
                            minWidth: '60px',
                            whiteSpace: 'nowrap',
                          }}
                          title="Remove this user's access entirely"
                        >
                          {saving === perm.id ? '...' : 'Revoke'}
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  subtitle: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  delegateBadge: {
    background: '#D6E4F0',
    color: '#1B3A5C',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: '600'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    color: '#2E75B6'
  },
  errorBox: {
    background: '#FCE4D6', color: '#C0392B',
    padding: '10px 16px', borderRadius: '6px',
    marginBottom: '16px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center'
  },
  successBox: {
    background: '#E2F0D9', color: '#1E7B34',
    padding: '10px 16px', borderRadius: '6px',
    marginBottom: '16px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'center'
  },
  closeBtn: {
    background: 'none', border: 'none',
    cursor: 'pointer', fontSize: '14px', opacity: 0.7
  },
  formTitle: {
    fontSize: '16px', fontWeight: '600',
    color: '#1B3A5C', marginBottom: '16px',
    display: 'flex', alignItems: 'center', gap: '8px'
  },
  count: {
    fontSize: '12px', fontWeight: '400',
    background: '#D6E4F0', color: '#1B3A5C',
    padding: '2px 8px', borderRadius: '12px'
  },
  hint: {
    fontSize: '12px', color: '#595959', marginTop: '6px'
  },
  presetBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  },
  presetLabel: {
    fontSize: '12px',
    color: '#666',
    marginRight: '4px',
  },
  presetBtn: {
    fontSize: '12px',
    padding: '3px 10px',
    borderRadius: '4px',
  },
  permGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '8px',
    marginTop: '16px',
    padding: '16px',
    background: '#f9f9f9',
    borderRadius: '8px'
  },
  permLabel: {
    gridColumn: '1 / -1',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1B3A5C',
    marginBottom: '4px'
  },
  permCheck: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#1B3A5C'
  },
  permCheckLocked: {
    cursor: 'not-allowed',
    opacity: 0.55
  },
  ownerTag: {
    marginLeft: '6px',
    fontSize: '10px',
    background: '#FFF2CC',
    color: '#B7600A',
    padding: '1px 6px',
    borderRadius: '4px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.3px'
  },
  ownerTagHeader: {
    marginLeft: '4px',
    fontSize: '9px',
    background: '#FFF2CC',
    color: '#B7600A',
    padding: '1px 4px',
    borderRadius: '3px',
    fontWeight: '600',
    textTransform: 'uppercase',
    verticalAlign: 'middle'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f0f0f0'
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '10px 12px',
    background: '#1B3A5C',
    color: 'white',
    fontWeight: '600',
    textAlign: 'left',
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },
  thPerm: {
    padding: '10px 12px',
    background: '#1B3A5C',
    color: 'white',
    fontWeight: '600',
    textAlign: 'center',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    minWidth: '80px'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0'
  },
  td: {
    padding: '10px 12px',
    fontSize: '13px'
  },
  tdPerm: {
    padding: '10px 12px',
    textAlign: 'center'
  },
  userName: {
    fontWeight: '500',
    color: '#1B3A5C'
  },
  userEmail: {
    fontSize: '12px',
    color: '#595959'
  },
  toggle: {
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    color: 'white',
    fontWeight: '700',
    fontSize: '14px',
    transition: 'all 0.2s'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: '#595959',
    fontSize: '14px'
  }
};

export default ACL;
